import { useState } from "react";
import { Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, AlertCircle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/api";

export default function AdminLogin() {
  const { user, login } = useAuth();
  const [form, setForm] = useState({ username: "", password: "" });
  const [status, setStatus] = useState({ state: "idle", msg: "" });

  if (user) return <Navigate to="/admin/dashboard" replace />;
  if (user === undefined) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-cream font-mono-label text-[var(--k-muted)]" data-testid="admin-loading">
        Checking session…
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setStatus({ state: "loading", msg: "" });
    try {
      await login(form.username.trim(), form.password);
      setStatus({ state: "success", msg: "" });
    } catch (err) {
      setStatus({ state: "error", msg: formatApiError(err?.response?.data?.detail) || "Invalid credentials." });
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-cream px-6" data-testid="admin-login">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-1/3 w-[500px] h-[500px] rounded-full bg-[var(--k-olive-soft)] opacity-30 blur-[120px]" />
        <div className="absolute bottom-1/4 right-1/3 w-[400px] h-[400px] rounded-full bg-[var(--k-sand)] opacity-40 blur-[120px]" />
      </div>
      <motion.form
        onSubmit={submit}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="organic-card p-10 md:p-12 w-full max-w-md relative z-10"
        data-testid="admin-login-form"
      >
        <div className="w-14 h-14 rounded-full bg-[var(--k-green)] flex items-center justify-center mb-7">
          <Lock className="w-6 h-6 text-[var(--k-bg)]" strokeWidth={1.5} />
        </div>
        <div className="font-mono-label text-[var(--k-gold)] mb-2">KOSTRA Admin</div>
        <h1 className="font-serif-display text-3xl text-[var(--k-text)] mb-8">Sign in</h1>

        <label className="flex flex-col gap-2 mb-5">
          <span className="font-mono-label text-[var(--k-muted)]">Username</span>
          <input
            type="text"
            required
            autoFocus
            value={form.username}
            onChange={(e) => setForm((p) => ({ ...p, username: e.target.value }))}
            className="kostra-input"
            data-testid="admin-input-username"
            autoComplete="username"
          />
        </label>
        <label className="flex flex-col gap-2 mb-7">
          <span className="font-mono-label text-[var(--k-muted)]">Password</span>
          <input
            type="password"
            required
            value={form.password}
            onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))}
            className="kostra-input"
            data-testid="admin-input-password"
            autoComplete="current-password"
          />
        </label>

        {status.state === "error" && (
          <div className="flex items-start gap-3 border border-red-300 bg-red-50 p-3 rounded mb-5" data-testid="admin-login-error">
            <AlertCircle className="w-4 h-4 text-red-600 mt-0.5" />
            <p className="text-[var(--k-text)] text-xs">{status.msg}</p>
          </div>
        )}

        <button
          type="submit"
          disabled={status.state === "loading"}
          className="btn-green w-full"
          data-testid="admin-submit-btn"
        >
          {status.state === "loading" ? "Signing in…" : "Sign In"}
        </button>
      </motion.form>
    </div>
  );
}
